import React, { useState, useEffect, useRef } from 'react'
import api from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { 
  User, 
  Phone, 
  Camera, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  X
} from 'lucide-react'

interface ProfileFormData {
  phoneNumber: string
  skills: string[]
}

const COMMON_SKILLS = [
  'School schaatsbegeleiding',
  'Kassa / schaatsverhuur',
  'Toezichthouder',
  'Passe-partouts controle',
  'Op- afbouw',
  'Technische ondersteuning',
  'BHV',
  'Social media content creatie',
  'Fotograaf',
  'Video producent'
]

const CompleteProfilePage: React.FC = () => {
  const { t } = useLocale()
  const { user, profile, profileLoading, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasRendered = useRef(false)
  
  // Prevent infinite re-renders
  if (!hasRendered.current) {
    console.log('CompleteProfilePage component rendering')
    hasRendered.current = true
  }
  
  const [formData, setFormData] = useState<ProfileFormData>({
    phoneNumber: '',
    skills: []
  })
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    console.log('CompleteProfilePage useEffect - user:', !!user, 'profile:', !!profile, 'profileLoading:', profileLoading)
    
    // Only run redirect logic if we have all the data
    if (profileLoading) {
      console.log('CompleteProfilePage: still loading profile, waiting...')
      return
    }
    
    // Redirect if user is not logged in
    if (!user) {
      console.log('CompleteProfilePage: no user, redirecting to login')
      navigate('/login')
      return
    }

    // Redirect if profile is already completed
    if (profile?.profile_completed) {
      console.log('CompleteProfilePage: profile already completed, redirecting to dashboard')
      navigate('/dashboard')
      return
    }

    // Redirect admins to dashboard (they don't need to complete volunteer application)
    if (profile?.role === 'admin') {
      console.log('CompleteProfilePage: admin detected, redirecting to dashboard')
      navigate('/dashboard')
      return
    }

    // Load existing profile data
    if (profile) {
      setFormData(prev => ({
        ...prev,
        phoneNumber: profile.phone_number || '',
        skills: profile.skills || []
      }))
      
      if (profile.avatar_url) {
        setAvatarPreview(profile.avatar_url)
      }
    }
  }, [user, profile, profileLoading, navigate])


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSkillToggle = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }))
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file')
        return
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB')
        return
      }
      
      setAvatarFile(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile) return null
    const res = await api.profiles.uploadAvatar(avatarFile)
    return res?.avatar_url ?? null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    // Validation
    if (!formData.phoneNumber) {
      setError('Please fill in your phone number')
      setLoading(false)
      return
    }

    try {
      let avatarUrl: string | null = null
      if (avatarFile) {
        avatarUrl = await uploadAvatar()
      }

      await api.profiles.updateMe({
        phone_number: formData.phoneNumber,
        skills: formData.skills,
        profile_completed: true,
        ...(avatarUrl && { avatar_url: avatarUrl })
      })

      await refreshProfile()
      setSuccess(profile?.role === 'admin' ? 'Profile completed successfully! Welcome to the admin dashboard.' : 'Profile completed successfully! You can now apply to be a volunteer.')
      setTimeout(() => navigate('/dashboard'), 2000)
    } catch (error: any) {
      console.error('Error updating profile:', error)
      setError(error.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading user...</p>
        </div>
      </div>
    )
  }

  if (!profile && profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
          <p className="text-sm text-gray-500 mt-2">Creating your profile...</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    )
  }


  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <User className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t('profile.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Welkom {profile?.first_name || 'daar'}! Vul je profiel aan om verder te gaan.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-md bg-green-50 p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Avatar Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.avatar')} ({t('profile.optional')})
              </label>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  {avatarPreview ? (
                    <div className="relative">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="h-20 w-20 rounded-full object-cover border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={removeAvatar}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                      <Camera className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {avatarPreview ? t('profile.changeAvatar') : t('profile.uploadAvatar')}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG or WebP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                  {t('profile.phoneNumber')} <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    required
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+31 6 12345678"
                  />
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('profile.skills')} ({t('profile.optional')})
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {COMMON_SKILLS.map(skill => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => handleSkillToggle(skill)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      formData.skills.includes(skill)
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
              {formData.skills.length > 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  Geselecteerd: {formData.skills.join(', ')}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="border-t pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {loading ? t('profile.completing') : t('profile.completeProfile')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CompleteProfilePage
